/**
 * Pre-minted `lt-` client order ids (speed-demon Deploy C, browser pre-send —
 * docs/260904Node1SpeedDemonPlan.md §5).
 *
 * Every one-click / hotkey submission carries a fresh `lt-<uuid>` client
 * order id so relay audit rows and the venue echo join back to the input.
 * Minting it inline put `crypto.randomUUID()` between the pacing fence and
 * the fetch; this keeps a small pool of ids minted ahead of time on idle so
 * the submit path only pops a string. When the pool is dry (first order
 * before idle ran, a burst larger than the pool, fake timers in tests) it
 * mints inline — the format and uniqueness contract are identical.
 */

const POOL_SIZE = 8;
const _pool: string[] = [];
let _refillPending = false;

function mint(): string {
  return `lt-${crypto.randomUUID()}`;
}

function refill(): void {
  _refillPending = false;
  while (_pool.length < POOL_SIZE) _pool.push(mint());
}

function scheduleRefill(): void {
  if (_refillPending || _pool.length >= POOL_SIZE) return;
  _refillPending = true;
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(refill, { timeout: 250 });
  } else {
    setTimeout(refill, 0);
  }
}

/** Next `lt-` client order id: a pre-minted one when the pool has it, an
 *  inline mint otherwise. Never blocks; always schedules an idle refill. */
export function nextClientOrderId(): string {
  const id = _pool.pop() ?? mint();
  scheduleRefill();
  return id;
}

/** Test seam: drop every pooled id and cancel the pending-refill flag. */
export function _resetCoidPoolForTests(): void {
  _pool.length = 0;
  _refillPending = false;
}

/** Test seam: how many ids are pooled right now. */
export function _coidPoolSizeForTests(): number {
  return _pool.length;
}

// Warm the pool on module load so the first order after boot finds it full.
scheduleRefill();
